import { Component, Input, OnInit } from '@angular/core'
import { PublicOrganization } from '@se-po/shared-data-access-models'
import { AffiliationService, OrganizationService, RoleAssignmentService } from '@se-po/shared-data-access-services'
import { lastValueFrom } from 'rxjs'
import { SeFeToastService, SeFeToastTypes } from 'se-fe-toast'
import { RoleAssignment } from 'se-resource-types/lib/UserService'

interface AffiliationAccessOrg extends PublicOrganization {
  loading: boolean
  topDownAccessRole: RoleAssignment
}

@Component({
  selector: 'se-po-affiliation-access',
  templateUrl: './affiliation-access.component.html',
  styleUrls: ['./affiliation-access.component.scss'],
  standalone: false,
})
export class AffiliationAccessComponent implements OnInit {

  @Input() public orgId: number
  @Input() public rootOrgIds?: number[]

  public ancestorOrgs: AffiliationAccessOrg[]
  public loaded: boolean

  constructor(
    private affiliationService: AffiliationService,
    private organizationService: OrganizationService,
    private roleAssignmentService: RoleAssignmentService,
    private toastService: SeFeToastService
  ) { }

  public async ngOnInit(): Promise<void> {
    const { result: affiliations } = await lastValueFrom(this.affiliationService.search(this.orgId)) as any

    if (affiliations.length) {
      // ensure each affiliation is a descendant of one of passed in rootOrgIds
      const ancestorOrgIds = [...new Set(affiliations.filter((aff) => !this.rootOrgIds ||
          aff.ancestor_organization_ids.some((id) => this.rootOrgIds.includes(id))
      ).flatMap((aff) => aff.ancestor_organization_ids))] as number[]

      this.ancestorOrgs = await lastValueFrom(this.organizationService.indexPublic(ancestorOrgIds)) as AffiliationAccessOrg[]
      const roles = await lastValueFrom(this.roleAssignmentService.getTopDownOrgAccessRoles(this.orgId))
      this.ancestorOrgs.forEach((org) => {
        org.topDownAccessRole = roles.find((role) => role.principal_id === org.id)
      })
    }

    this.loaded = true
  }

  public async toggleTopDownAccess(allowAccess: boolean, org: AffiliationAccessOrg): Promise<void> {
    org.loading = true
    try {
      if (allowAccess) {
        await this.addRole(org)
      } else if (org.topDownAccessRole) {
        await this.deleteRole(org)
      }
    } catch (e) {
      console.error(e)
      this.toastService.addToast({
        type: SeFeToastTypes.Error,
        message: 'Error when updating Affiliation Access'
      })
    }
    org.loading = false
  }

  private async addRole(org: AffiliationAccessOrg): Promise<void> {
    org.topDownAccessRole = await this.roleAssignmentService.grantTopDownOrgAccess(this.orgId, org.id).toPromise()
    this.toastService.addToast({
      type: SeFeToastTypes.Success,
      message: `${org.name} Affiliation Access has been granted`
    })
  }

  private async deleteRole(org: AffiliationAccessOrg): Promise<void> {
    await this.roleAssignmentService.deleteRole(org.topDownAccessRole.id).toPromise()
    org.topDownAccessRole = null
    this.toastService.addToast({
      type: SeFeToastTypes.Success,
      message: `${org.name} Affiliation Access has been denied`
    })
  }

}
